<template>
  <v-dialog v-model="showDialog" width="500px">
    <v-card class="search-modal">
      <h2 class="mb-3">Search & Add Sires</h2>
      <v-form class="search-form" @submit.prevent="search">
        <v-text-field
          outlined
          prepend-inner-icon="mdi-magnify"
          v-model="dogID"
          label="Search Dog ID"
        ></v-text-field>
      </v-form>
      <p v-for="(error, idx) in errors" :key="idx">{{error}}</p>
      <v-data-table
        v-if="searchResults.length"
        :headers="headers"
        :items="searchResults"
        hide-default-footer
        hide-default-header
      >
        <template v-slot:item="row">
          <tr v-bind="attrs" v-on="on">
            <td>{{ row.item.name }}</td>
            <td>{{ row.item.mainColor }}</td>
            <td>{{ row.item.dogId }}</td>
            <td>
              <a v-on:click="addSire(row.item)">
                <font-awesome-icon icon="plus" color="primary" />
              </a>
            </td>
          </tr>
        </template>
      </v-data-table>
      <p class="mb-6" v-if="selectedSires.length">- or -</p>
      <v-btn
        color="primary"
        class="white--text"
        block
        :disabled="!selectedSires.length"
        v-on:click="submit"
        >Continue</v-btn
      >
      <div v-if="selectedSires.length">
        <h2 class="mt-8">Added Sires</h2>
        <v-data-table
          :headers="headers"
          :items="selectedSires"
          hide-default-footer
          hide-default-header
        >
          <template v-slot:item="row">
            <tr>
              <td>{{ row.item.name }}</td>
              <td>{{ row.item.mainColor }}</td>
              <td>{{ row.item.dogId }}</td>
              <td>
                <a v-on:click="removeSire(row.item)">
                  <font-awesome-icon icon="minus" />
                </a>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
      <p class="mt-8">
        The signature of all sires' owner and co-owner(s) (if applicable) are
        required to register a litter. An email with a link to the appropriate
        form will be sent to the addresses listed for each owner and co-owner.
        Registration is not complete until the form is submitted and the sire's
        DNA test results are posted.
        <br />
        *In the case of a multi-sire litter, a DNA profile must be uploaded for
        each puppy in order to verify their Sire. An ASCA admin will review each
        puppy's DNA profile before approving the litter registration.
      </p>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "throttle-debounce";
import registerLitterService from "@/services/portal/registerLitterService";
import { getErrorMessage } from "@/services/errorHandler";

export default Vue.extend({
  name: "AddSireModal",
  data: function () {
    return {
      showDialog: this.sireDialog,
      hasSearched: false,
      searchResults: [] as object[],
      errors: [] as string[],
      selectedSires: [...this.sireInfo],
      dogID: "",
      headers: [
        { text: "Name", align: "left", value: "name" },
        { text: "Color", align: "left", value: "mainColor" },
        {
          text: "Dog ID",
          align: "left",
          value: "dogId",
        },
      ],
    };
  },
  watch: {
    sireDialog: function (newVal: boolean) {
      if (!newVal) {
        this.dogID = "";
        this.hasSearched = false;
        this.searchResults = [];
      }
      this.showDialog = newVal;
    },
    showDialog: function (newVal: boolean) {
      //this is handling situation when the user closes dialog
      //by clicking outside of it
      if (!newVal) {
        this.dogID = "";
        this.hasSearched = false;
        this.searchResults = [];
        this.selectedSires = [...this.sireInfo];
        this.toggleSireModal();
      }
    },
    dogID: {
      handler: debounce(
        1000,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal || !newVal) return;
          this.search();
        }
      ),
      deep: true,
    },
  },
  methods: {
    search: function () {
      this.hasSearched = false;
      registerLitterService
        .searchSire(this.dogID)
        .then((response: any) => {
          this.searchResults = response.data ? [response.data] : [];
          this.errors = response.errors ? response.errors : [];
        })
        .catch((error) => {
          this.searchResults = [];
          this.errors = [getErrorMessage(error)];
        })
        .finally(() => {
          this.hasSearched = true;
        });
    },
    addSire(row: any) {
      const found = this.selectedSires.some((el: any) => el.dogId === row.dogId);
      if (found) return;

      this.searchResults = [];
      this.hasSearched = false;
      this.dogID = "";

      const owners = [] as any;
      row.primaryOwners.forEach((o: any) => owners.push({PersonId: o.personId, IsPrimary: o.isPrimary}))
      let item = {
        name: row.name,
        dogId: row.dogId,
        mainColor: row.mainColor,
        owners: owners,
        dnaSubmitted: row.dnaSubmitted,
      };

      this.selectedSires.push(item);
    },
    removeSire(row: any) {
      this.selectedSires.splice(this.selectedSires.indexOf(row), 1);
      this.changeSireInfo(this.selectedSires);
      this.changePuppyInfo([], row.dogId, true);
    },
    submit() {
      this.changeSireInfo(this.selectedSires);
      this.showDialog = false;
    },
  },
  props: {
    changeSireInfo: Function,
    sireDialog: Boolean,
    toggleSireModal: Function,
    sireInfo: Array,
    changePuppyInfo: Function,
  },
});
</script>
